import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import Menu from '@components/Menu';
import customersService from '@services/customers';
import ccService from '@services/cc';
import { MdVisibility, MdExpandMore, MdExpandLess, MdPrint, MdFileDownload } from 'react-icons/md';
import UserContext from '@components/context/userContext';
import { TimestampToDate } from '@utils/date';
import Input from "@components/Input";
import AutoCompleteField from "@components/AutoCompleteV2";
import PrintTemplate from './print';
import PDFPrint from "@components/PdfPrint";
import { showGlobalLoading, hideGlobalLoading } from "@services/axiosInstance";
import * as XLSX from 'xlsx';

const PendingDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [startdate, setStartdate] = useState('');
  const [enddate, setEnddate] = useState('');
  const [debouncedStartdate, setDebouncedStartdate] = useState('');
  const [debouncedEnddate, setDebouncedEnddate] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printType, setPrintType] = useState(null);
  const [printCustomer, setPrintCustomer] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const printerRef = useRef(null);
  const profile = useContext(UserContext);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await customersService.get(profile.idEmpresa);
        setCustomers(customers || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };

    fetchCustomers();
  }, [profile.idEmpresa]);

  // Debounce date values changes (3 seconds)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedStartdate(startdate);
      setDebouncedEnddate(enddate);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [startdate, enddate]);

  // Helper function to convert date to unix timestamp
  const dateToUnixTimestamp = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return Math.floor(date.getTime() / 1000);
  };

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const customerId = selectedCustomer?.idcustomer;
      
      // Convert dates to unix timestamp if provided (using debounced values)
      const startdateTimestamp = debouncedStartdate ? dateToUnixTimestamp(debouncedStartdate) : null;
      const enddateTimestamp = debouncedEnddate ? dateToUnixTimestamp(debouncedEnddate) : null;
      
      const response = await ccService.get(profile.idEmpresa, {
        customerId: customerId,
        startdate: startdateTimestamp,
        enddate: enddateTimestamp
      });

      if (!response || !response.customers) {
        setDocuments([]);
        return;
      }

      const transformedData = Object.entries(response.customers).map(([idcustomer, customerData]) => ({
        customer: {
          idcustomer: idcustomer,
          name: customerData.nome_1,
          nif: customerData.nif,
          total: parseFloat(customerData.total || 0),
          count: customerData.items ? Object.keys(customerData.items).length : 0
        },
        documents: Object.values(customerData.items || {}).map(doc => ({
          type: doc.tipo,
          posto: doc.posto,
          tpv: doc.tpv,
          serie: doc.serie,
          doc: doc.doc,
          diff: ['notas_credito'].includes(doc.tipo) ? -doc.diff : doc.diff,
          numero: doc.numero,
          date_time: doc.date_time,
          total: doc.valor_liquido
        }))
      }));

      setDocuments(transformedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Erro ao carregar documentos pendentes');
      setLoading(false);
    }
  }, [profile.idEmpresa, selectedCustomer, debouncedStartdate, debouncedEnddate]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const toggleCustomer = (customerNif) => {
    setExpandedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerNif)) {
        newSet.delete(customerNif);
      } else {
        newSet.add(customerNif);
      }
      return newSet;
    });
  };

  const totalValue = documents.reduce((acc, { customer }) => acc + customer.total, 0);
  const totalDocuments = documents.reduce((acc, { customer }) => acc + customer.count, 0);

  const handleCCPrint = async (customer = null) => {
    showGlobalLoading('A preparar documento para impressão...');
    
    try {
      // Construir URL do PDF baseado no tipo
      let pdfUrl = `https://eposgestofinal.com/api/cc/pdf.php?idempresa=${profile.idEmpresa}`;
      
      // Adicionar customerId apenas se existir (pode ser do parâmetro ou do selectedCustomer)
      const customerId = customer || selectedCustomer?.idcustomer;
      if (customerId) {
        pdfUrl += `&customerId=${customerId}`;
      }
      
      // Adicionar datas se fornecidas (using debounced values)
      const startdateTimestamp = debouncedStartdate ? dateToUnixTimestamp(debouncedStartdate) : null;
      const enddateTimestamp = debouncedEnddate ? dateToUnixTimestamp(debouncedEnddate) : null;
      
      if (startdateTimestamp) {
        pdfUrl += `&startdate=${startdateTimestamp}`;
      }
      
      if (enddateTimestamp) {
        pdfUrl += `&enddate=${enddateTimestamp}`;
      }
      
      // Verificar se a URL é válida antes de continuar
      if (!pdfUrl || !pdfUrl.startsWith('https://')) {
        throw new Error('URL do PDF inválida');
      }
      
      // Atualizar a URL primeiro
      setPreviewUrl(pdfUrl);
      
      // Aguardar múltiplos ciclos de render para garantir que o componente foi renderizado com a nova URL
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verificar se o componente está disponível
      if (!printerRef.current) {
        throw new Error('Componente de impressão não disponível');
      }
      
      // Chamar o componente de impressão
      await printerRef.current.handlePrint();
      hideGlobalLoading();
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      hideGlobalLoading();
      alert('Erro ao preparar documento para impressão. Por favor, tente novamente.');
    }
  };

  const handleExportExcel = (type, customer = null) => {
    let data = [];
    let filename = '';

    if (type === 'totals') {
      // Export summary of all customers
      data = [
        ['Cliente', 'NIF', 'Documentos', 'Total'],
        ...documents.map(({ customer }) => [
          customer.name,
          customer.nif || '',
          customer.count,
          customer.total.toFixed(2)
        ]),
        ['Total', '', totalDocuments, totalValue.toFixed(2)]
      ];
      filename = 'documentos-pendentes-resumo.xlsx';
    } else if (type === 'customer') {
      // Export details for a specific customer
      const customerData = documents.find(d => d.customer.name === customer.name);
      if (!customerData) return;

      data = [
        ['Data', 'Documento', 'Total'],
        ...customerData.documents.map(doc => [
          TimestampToDate(doc.date_time),
          doc.doc,
          parseFloat(doc.total).toFixed(2)
        ]),
        ['Total', '', customerData.customer.total.toFixed(2)]
      ];
      filename = `documentos-pendentes-${customer.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-')}.xlsx`;
    } else {
      // Export all documents from all customers
      data = [
        ['Cliente', 'NIF', 'Data', 'Documento', 'Total'],
        ...documents.flatMap(({ customer, documents }) =>
          documents.map(doc => [
            customer.name,
            customer.nif || '',
            TimestampToDate(doc.date_time),
            doc.doc,
            parseFloat(doc.total).toFixed(2)
          ])
        ),
        ['Total', '', '', '', totalValue.toFixed(2)]
      ];
      filename = 'documentos-pendentes-detalhado.xlsx';
    }

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Documentos Pendentes');
    XLSX.writeFile(wb, filename);
  };

  const renderPrintContent = () => {
    const timestamp = new Date().toLocaleString('pt-PT');
    
    if (printType === 'totals') {
      return (
        <>
          <div className="totals">
            <h2>Resumo</h2>
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th style={{ textAlign: 'right' }}>Documentos</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(({ customer }) => (
                  <tr key={customer.name}>
                    <td>{customer.name}</td>
                    <td style={{ textAlign: 'right' }}>{customer.count}</td>
                    <td style={{ textAlign: 'right' }}>{customer.total.toFixed(2)} €</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{totalDocuments}</td>
                  <td style={{ textAlign: 'right' }}>{totalValue.toFixed(2)} €</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="timestamp">
            Gerado em {timestamp}
          </div>
        </>
      );
    }

    if (printType === 'customer') {
      const customerData = documents.find(d => d.customer.name === printCustomer.name);
      if (!customerData) return null;

      return (
        <div className="customer-section">
          <div className="customer-header">
            <div>
              <div className="customer-name">{customerData.customer.name}</div>
              <div>NIF: {customerData.customer.nif || 'N/A'}</div>
            </div>
            <div className="customer-total" style={{ textAlign: 'right' }}>
              Total: {customerData.customer.total.toFixed(2)} €
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Documento</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {customerData.documents.map(doc => (
                <tr key={`${doc.type}-${doc.serie}-${doc.doc}`}>
                  <td>{TimestampToDate(doc.date_time)}</td>
                  <td>{doc.doc}</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(doc.total).toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="timestamp">
            Gerado em {timestamp}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div>
        <Menu />
        <div className="container mx-auto px-4 py-8" style={{
              marginTop: '75px',
            }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Menu />
        <div className="container mx-auto px-4 py-8" style={{
              marginTop: '75px',
            }}>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
            <p>Experimente com outros filtros</p>
            <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-end',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: '1', maxWidth: '400px' }}>
                      <AutoCompleteField
                        options={customers}
                        value={selectedCustomer}
                        onChange={(_, customer) => setSelectedCustomer(customer)}
                        getOptionLabel={(option) => `[${option.idcustomer}] ${option.name}` || ""}
                        label="Cliente"
                        searchableFields={['name', 'idcustomer', 'nif', 'email', 'phone', 'mobile', 'fax']}
                      />
                    </div>
                    <div style={{ width: '180px' }}>
                      <Input
                        title="Data Início"
                        inputType="date"
                        name="startdate"
                        value={startdate}
                        handleChange={(e) => setStartdate(e.target.value)}
                      />
                    </div>
                    <div style={{ width: '180px' }}>
                      <Input
                        title="Data Fim"
                        inputType="date"
                        name="enddate"
                        value={enddate}
                        handleChange={(e) => setEnddate(e.target.value)}
                      />
                    </div>
                  </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: '100%' }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: 'left',
              marginTop: '75px',
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li className="active">Documentos Pendentes</li>
          </ol>
        </div>
      </div>

      <div className="fullContainer" style={{ maxWidth: '100%', marginBottom: '2rem' }}>
        <div className="col-sm-12">
          <div className="panel panel-default">
            <div className="panel-heading" style={{ padding: '1.5rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  flex: '1'
                }}>
                  <h1 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    margin: 0,
                  }}>
                    Documentos Pendentes
                  </h1>
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-end',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: '1', maxWidth: '400px' }}>
                      <AutoCompleteField
                        options={customers}
                        value={selectedCustomer}
                        onChange={(_, customer) => setSelectedCustomer(customer)}
                        getOptionLabel={(option) => `[${option.idcustomer}] ${option.name}` || ""}
                        label="Cliente"
                        searchableFields={['name', 'idcustomer', 'nif', 'email', 'phone', 'mobile', 'fax']}
                      />
                    </div>
                    <div style={{ width: '180px' }}>
                      <Input
                        title="Data Início"
                        inputType="date"
                        name="startdate"
                        value={startdate}
                        handleChange={(e) => setStartdate(e.target.value)}
                      />
                    </div>
                    <div style={{ width: '180px' }}>
                      <Input
                        title="Data Fim"
                        inputType="date"
                        name="enddate"
                        value={enddate}
                        handleChange={(e) => setEnddate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2rem'
                }}>
                    <div style={{
                        textAlign: 'right',
                        borderRight: '1px solid #e5e7eb',
                        paddingRight: '2rem',
                        display: 'flex', gap: '0.5rem'
                    }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCCPrint();
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                        >
                          <MdPrint style={{
                                    fontSize: "15px",
                                    cursor: "pointer",
                                    transition: "color 0.2s",
                                    color: "rgb(100, 116, 139)",
                                  }}
                                  size={20}
                                  onMouseEnter={(e) => (e.target.style.color = "#408EE0")}
                                  onMouseLeave={(e) => (e.target.style.color = "rgb(100, 116, 139)")} />
                        </button>
                        <button
                          onClick={() => handleExportExcel('totals')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                        >
                          <MdFileDownload style={{
                                    fontSize: "15px",
                                    cursor: "pointer",
                                    transition: "color 0.2s",
                                    color: "rgb(100, 116, 139)",
                                  }}
                                  size={20}
                                  onMouseEnter={(e) => (e.target.style.color = "#408EE0")}
                                  onMouseLeave={(e) => (e.target.style.color = "rgb(100, 116, 139)")} />
                        </button>
                      </div>
                  <div style={{
                    textAlign: 'right',
                    borderRight: '1px solid #e5e7eb',
                    paddingRight: '2rem'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      color: '#64748b',
                      marginBottom: '0.25rem'
                    }}>
                      Total Documentos
                    </div>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '600',
                      color: '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      justifyContent: 'flex-end'
                    }}>
                      {totalDocuments}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '1.5rem',
                      color: '#64748b',
                      marginBottom: '0.25rem'
                    }}>
                      Valor Total
                    </div>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {totalValue.toFixed(2)} €
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="panel-body" style={{ padding: '0' }}>
              {documents.map(({ customer, documents }) => (
                <div 
                  key={customer.name}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: 'white'
                  }}
                >
                  <div 
                    style={{
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      backgroundColor: expandedCustomers.has(customer.nif) ? '#f8fafc' : 'white'
                    }}
                    onClick={() => toggleCustomer(customer.nif)}
                  >
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 200px 48px',
                      alignItems: 'center',
                      gap: '2rem'
                    }}>
                      <div>
                        <h2 style={{
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          marginBottom: '0.25rem'
                        }}>
                          [{customer.idcustomer}] {customer.name}
                        </h2>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#64748b'
                        }}>
                          NIF: {customer.nif || 'N/A'}
                        </p>
                      </div>
                      
                      <div style={{ 
                        textAlign: 'right',
                        minWidth: '200px'
                      }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCCPrint(customer.idcustomer);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#64748b',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                            >
                              <MdPrint style={{
                                    fontSize: "15px",
                                    cursor: "pointer",
                                    transition: "color 0.2s",
                                    color: "rgb(100, 116, 139)",
                                  }}
                                  size={20}
                                  onMouseEnter={(e) => (e.target.style.color = "#408EE0")}
                                  onMouseLeave={(e) => (e.target.style.color = "rgb(100, 116, 139)")} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportExcel('customer', customer);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#64748b',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                            >
                              <MdFileDownload style={{
                                    fontSize: "15px",
                                    cursor: "pointer",
                                    transition: "color 0.2s",
                                    color: "rgb(100, 116, 139)",
                                  }}
                                  size={20}
                                  onMouseEnter={(e) => (e.target.style.color = "#408EE0")}
                                  onMouseLeave={(e) => (e.target.style.color = "rgb(100, 116, 139)")} />
                            </button>
                            <div style={{
                                    textAlign: 'right',
                                    borderLeft: '1px solid #e5e7eb',
                                    paddingLeft: '2rem'
                                }}>
                                <p style={{
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                justifyContent: 'flex-end'
                                }}>
                                    {customer.total.toFixed(2)} €
                                </p>
                                <p style={{
                                fontSize: '0.875rem',
                                color: '#64748b',
                                textAlign: 'right'
                                }}>
                                    {customer.count} documento{customer.count !== 1 ? 's' : ''}
                                </p>
                            </div>
                          </div>
                      </div>

                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        width: '48px'
                      }}>
                        {expandedCustomers.has(customer.nif) ? (
                          <MdExpandLess size={24} color="#64748b" />
                        ) : (
                          <MdExpandMore size={24} color="#64748b" />
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedCustomers.has(customer.nif) && (
                    <div style={{ 
                      borderTop: '1px solid #e5e7eb',
                      padding: '1.5rem'
                    }}>
                      <table className="table" style={{ 
                        margin: 0,
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}>
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Documento</th>
                            <th style={{ textAlign: 'right', width: '200px' }}>Total</th>
                            <th style={{ width: '48px', textAlign: 'center' }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map(doc => (
                            <tr key={`${doc.type}-${doc.serie}-${doc.doc}`}>
                              <td>{TimestampToDate(doc.date_time)}</td>
                              <td>{doc.doc}</td>
                              <td style={{ textAlign: 'right', width: '200px' }}>{parseFloat(doc.diff).toFixed(2)} €</td>
                              <td style={{ width: '48px', textAlign: 'center' }}>
                                <MdVisibility
                                  style={{
                                    fontSize: "15px",
                                    cursor: "pointer",
                                    transition: "color 0.2s",
                                    color: "rgb(100, 116, 139)",
                                  }}
                                  size={20}
                                  onMouseEnter={(e) => (e.target.style.color = "#408EE0")}
                                  onMouseLeave={(e) => (e.target.style.color = "rgb(100, 116, 139)")}
                                  onClick={() =>
                                    window.open(`/sales/${doc.type}${
                                        ["taloes"].includes(doc.type) ? `/${doc.posto}/${doc.tpv}` : ""
                                      }/${doc.serie}/${doc.numero}`, '_blank')
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PrintTemplate
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        title={printType === 'totals' ? 'Resumo de Documentos Pendentes' : `Documentos Pendentes - ${printCustomer?.name}`}
      >
        {renderPrintContent()}
      </PrintTemplate>

      {previewUrl && <PDFPrint key={previewUrl} ref={printerRef} pdfUrl={previewUrl} />}
    </div>
  );
};

export default PendingDocuments;